import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Badge,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import isMobile from "is-mobile";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import React, { useCallback, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import bnbIcon from "../../assets/imgs/bnb.png";
import familyIcon from "../../assets/imgs/layout/family-gray.png";
import profileIcon from "../../assets/imgs/layout/user.png";
import plsIcon from "../../assets/imgs/pls.png";

import Logo from "../../components/Logo";
import NotificationPanel from "./NotificationPanel";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useGlobalSound from "../../hook/useGlobalSound";
import useProfile from "../../hook/useProfile";
import useWallet from "../../hook/useWallet";
import { clearNotificationBadge } from "../../reducers/notification.slice";
import { AllegianceInvitationInfo } from "../../types/AllegianceInvitationInfo";
import { AllegianceRequestInfo } from "../../types/AllegianceRequestInfo";
import { StateSetter } from "../../types/Common";
import { FamilyInvitation } from "../../types/FamilyInvitation";
import { RankXPName } from "../../constants/rank";
import { shortAddress, toUSDFormat } from "../../utils/utils";

import { useStyles } from "./index.styles";

const HeaderV2 = ({
  setInvitationInfo,
  setOpenInviteModal,
  setAllegianceInvitationInfo,
  setOpenAllegianceInviteModal,
  setAllegianceRequestInfo,
  setOpenAllegianceRequestModal,
  toggleMobileSideBar,
}: {
  setInvitationInfo: StateSetter<FamilyInvitation>;
  setOpenInviteModal: StateSetter<boolean>;
  setAllegianceInvitationInfo: StateSetter<AllegianceInvitationInfo>;
  setOpenAllegianceInviteModal: StateSetter<boolean>;
  setAllegianceRequestInfo: StateSetter<AllegianceRequestInfo>;
  setOpenAllegianceRequestModal: StateSetter<boolean>;
  toggleMobileSideBar: StateSetter<boolean>;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { connectWallet, account, isConnected, disconnectWallet } = useWallet();
  const { myProfile, profileName } = useProfile(account);
  const { cashBalance, isInJail, helperCreditBalance, bulletBalance } =
    useAppSelector((state) => state.profile);
  const { notifications, notificationCount } = useAppSelector(
    (state) => state.notification
  );
  const [showNotification, toggleNotification] = useState<boolean>(false);
  const [anchorElNotification, setAnchorElNotification] =
    useState<null | HTMLElement>(null);
  const [, setOpenChat] = useState(false);

  const handleShowNotification = (event: React.MouseEvent<HTMLElement>) => {
    toggleNotification((previousOpen) => !previousOpen);
    setAnchorElNotification(event.currentTarget);

    if (notifications.length === 0) return;

    dispatch(clearNotificationBadge());
  };
  const onConnectWallet = useCallback(() => {
    setOpenChat(false);
    connectWallet();
  }, [connectWallet]);

  const { muted, toggleMute } = useGlobalSound();

  const rankPercent = Math.floor(myProfile.rankPercent || 0);

  const renderRankInfo = () => {
    return (
      <Box className={classes.menuListBody}>
        <NavLink
          to={isInJail ? "/" : `/rank`}
          className={classes.userProfileBtn}
        >
          <Tooltip
            title={
              <>
                {RankXPName[myProfile.rankNumber ? myProfile.rankNumber : 0] ||
                  ""}
              </>
            }
          >
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {Object.keys(myProfile).length === 0
                ? "No Profile"
                : RankXPName[
                    myProfile.rankNumber ? myProfile.rankNumber - 1 : 0
                  ]}
            </Typography>
          </Tooltip>

          <Box className={classes.progress} sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={rankPercent}
              className={classes.progressBar}
            />
            <Typography variant="body2" className={classes.progressLabel}>
              {`${rankPercent}%`}
            </Typography>
          </Box>

          <Box
            component="img"
            width={18}
            sx={{ marginLeft: "8px", verticalAlign: "sub" }}
            src={"/assets/imgs/layout/user.png"}
          ></Box>
        </NavLink>
      </Box>
    );
  };

  const renderBalances = () => {
    if (!account) return <></>;

    const balanceList = [
      {
        tooltip: "Health History",
        name: "Health",
        balance: 0,
        link: isInJail ? `/` : `#`,
        icon: "/assets/imgs/inventory/healthitem.png",
      },

      {
        tooltip: "Bullet History",
        name: "Bullets",
        balance: bulletBalance,
        link: isInJail ? `/` : `/bullet/history`,
        icon: "/assets/imgs/inventory/bulletitem.png",
      },

      {
        tooltip: "Credit History",
        name: "Credits",
        balance: helperCreditBalance,
        link: isInJail ? `/` : `/credit/history`,
        icon: "/assets/imgs/inventory/helperitem.png",
      },

      {
        tooltip: "Cash History",
        name: "Cash",
        balance: cashBalance,
        link: isInJail ? `/` : `/cash/history`,
        icon: "/assets/imgs/layout/currency.png",
      },
    ];

    return (
      <Box className={classes.balance}>
        {balanceList.map((balance, index) => (
          <Tooltip title={balance.tooltip} key={index}>
            <Box className={classes.balanceContainer}>
              <Typography
                textAlign={"center"}
                fontWeight={900}
                color={"gray"}
                fontSize={14}
                textTransform={"uppercase"}
              >
                {balance.name}
              </Typography>
              <Link to={balance.link} className={classes.currency}>
                <Box component={"img"} width={24} src={balance.icon}></Box>
                {toUSDFormat(balance.balance, 0)}
              </Link>
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  const renderUserInfo = () => {
    return (
      <Box className={classes.menuListBody}>
        <NavLink
          to={isInJail ? "/" : `profile/${profileName}`}
          className={classes.userProfileBtn}
        >
          <Tooltip title={<>{account}</> || ""}>
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {Object.keys(myProfile).length === 0
                ? isMobile()
                  ? shortAddress(account, 3, 3)
                  : shortAddress(account)
                : myProfile.name}
            </Typography>
          </Tooltip>

          <Box
            component="img"
            width={18}
            sx={{ marginLeft: "8px", verticalAlign: "sub" }}
            src={"/assets/imgs/layout/user.png"}
          ></Box>
        </NavLink>
      </Box>
    );
  };

  const renderConnectButton = () => {
    return (
      <Tooltip title={isConnected ? "Disconnect" : "Connect"}>
        <Button className={classes.connectWalletBtn} onClick={onConnectWallet}>
          {isMobile() ? (
            <>
              <Box
                component={"img"}
                width={24}
                src="/assets/imgs/layout/wallet.png"
              />
            </>
          ) : (
            <Typography component="span" sx={{ fontWeight: 700 }}>
              Connect Wallet
            </Typography>
          )}
        </Button>
      </Tooltip>
    );
  };

  return (
    <Box>
      <AppBar
        position="static"
        sx={{ background: "none", boxShadow: "none", zIndex: 1 }}
      >
        <Toolbar disableGutters className={classes.toolbar}>
          <Box
            className={classes.mdLogoWrapper}
            sx={{
              flexGrow: { md: 0, lg: 1 },
              justifyContent: { md: "center", lg: "flex-start" },
            }}
          >
            <Logo
              onClick={() => navigate("/")}
              sx={{
                width: { md: 80, xl: 100 },
                cursor: "pointer",
              }}
            />

            <Box>MAFIA</Box>
          </Box>

          <Box sx={{ flexGrow: 1, display: { md: "flex", lg: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={() => toggleMobileSideBar(true)}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {renderBalances()}

          <Box className={classes.userSection}>
            {isConnected ? (
              <>
                {renderRankInfo()}
                {renderUserInfo()}
              </>
            ) : (
              renderConnectButton()
            )}

            <Button className={classes.headerActionButton}>
              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <React.Fragment>
                    <Box
                      {...bindTrigger(popupState)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        component="img"
                        width={24}
                        src="/assets/imgs/layout/gear.png"
                      ></Box>
                    </Box>

                    <Menu
                      {...bindMenu(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      sx={{
                        "& .MuiPaper-root": {
                          backgroundColor: "#222222",
                          minWidth: "120px",
                        },
                        "& .MuiMenuItem-gutters": {
                          minHeight: "48px",
                        },
                        mt: 1.5,
                      }}
                    >
                      <MenuItem onClick={popupState.close}>
                        <Tooltip title="Chain Select" placement="left">
                          <Box
                            sx={{
                              display: "flex",
                              gap: "24px",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Box
                              component="img"
                              src={bnbIcon}
                              alt="BNB"
                              sx={{ width: 24, height: 24 }}
                              onClick={() => {
                                window.location.href = "https://bnbmafia.io";
                              }}
                            />
                            <Box
                              component="img"
                              src={plsIcon}
                              alt="PLS"
                              sx={{ width: 24, height: 24 }}
                              onClick={() => {
                                window.location.href = "https://pulsemafia.io";
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          popupState.close();
                          toggleMute();
                        }}
                      >
                        <Tooltip title="Mute Options" placement="left">
                          <Box
                            sx={{
                              display: "flex",
                              gap: "8px",
                              color: "white",
                              alignItems: "center",
                              fontFamily: "Philosopher",
                              fontWeight: 700,
                            }}
                          >
                            <Box
                              component={"img"}
                              src={
                                !muted
                                  ? "/assets/imgs/layout/mute.png"
                                  : "/assets/imgs/layout/volume.png"
                              }
                              width={21}
                              sx={{ cursor: "pointer" }}
                            ></Box>
                            {!muted ? "Mute" : "Unmute"}
                          </Box>
                        </Tooltip>
                      </MenuItem>

                      {myProfile.family && myProfile.family > 0 && (
                        <MenuItem
                          onClick={() => {
                            popupState.close();
                          }}
                        >
                          <Tooltip title="Go to Family" placement="left">
                            <NavLink
                              to={
                                isInJail
                                  ? "/"
                                  : `family/${myProfile.userFamily?.name}`
                              }
                              className={classes.profileLinkText}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "8px",
                                  color: "white",
                                  alignItems: "center",
                                  fontFamily: "Philosopher",
                                  fontWeight: 700,
                                }}
                              >
                                <Box
                                  component={"img"}
                                  src={familyIcon}
                                  width={21}
                                  sx={{ cursor: "pointer" }}
                                ></Box>
                                My family
                              </Box>
                            </NavLink>
                          </Tooltip>
                        </MenuItem>
                      )}

                      {myProfile && myProfile.name && (
                        <MenuItem onClick={popupState.close}>
                          <Tooltip title="Go to Profile" placement="left">
                            <Box>
                              <NavLink
                                to={
                                  isInJail ? "/" : `profile/${myProfile?.name}`
                                }
                                style={{
                                  fontFamily: "Philosopher",
                                  fontWeight: 700,
                                  textDecoration: "none",
                                  color: "white",
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  component={"img"}
                                  src={profileIcon}
                                  width={21}
                                ></Box>
                                My Profile
                              </NavLink>
                            </Box>
                          </Tooltip>
                        </MenuItem>
                      )}

                      <MenuItem
                        onClick={() => {
                          popupState.close();
                          isConnected ? disconnectWallet() : onConnectWallet();
                        }}
                      >
                        <Tooltip
                          title={isConnected ? "Disconnect" : "Connect"}
                          placement="left"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              fontFamily: "Philosopher",
                              fontWeight: 700,
                            }}
                          >
                            <Box
                              component="img"
                              width={24}
                              src="/assets/imgs/layout/power-off-gray.png"
                            ></Box>
                            {isConnected ? "Disconnect" : "Connect"}
                          </Box>
                        </Tooltip>
                      </MenuItem>
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            </Button>
            {isConnected && (
              <>
                <Button
                  className={classes.headerActionButton}
                  aria-describedby={"transition-popper"}
                  onClick={handleShowNotification}
                >
                  <Badge
                    badgeContent={notificationCount}
                    color="error"
                    sx={{ cursor: "pointer" }}
                  >
                    <Box
                      component="img"
                      src="/assets/imgs/layout/notification.png"
                      width={21}
                    />
                  </Badge>
                </Button>

                <NotificationPanel
                  account={account}
                  showNotification={showNotification}
                  anchorElNotification={anchorElNotification}
                  toggleNotification={toggleNotification}
                  setInvitationInfo={setInvitationInfo}
                  setOpenInviteModal={setOpenInviteModal}
                  setAllegianceInvitationInfo={setAllegianceInvitationInfo}
                  setOpenAllegianceInviteModal={setOpenAllegianceInviteModal}
                  setAllegianceRequestInfo={setAllegianceRequestInfo}
                  setOpenAllegianceRequestModal={setOpenAllegianceRequestModal}
                />
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default HeaderV2;
